import React, { forwardRef, useImperativeHandle } from 'react';
import { styled, useTheme, Typography } from '@mui/material';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  GetTestingDashboardLoginFormInitialValue, getTestingDashBoardLoginFormSchema, ITestingDashboardLoginFormFields,
} from '../../schemas';
import { FormField } from '../shared';
import { useEnterToSubmit } from '../../hooks';

const LoginTextField = styled(FormField<ITestingDashboardLoginFormFields>)(({ theme }) => ({
  width: '100%',
  marginTop: '15px',
}));

const LoginForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 15,
}));

const styleProp = {
  background: '#FFFEFE 0% 0% no-repeat padding-box',
  border: '1px solid #0000001F',
};

const inputStyleUserName = {
  style: styleProp,
  maxLength: 10,
};

const inputStylePassword = {
  style: styleProp,
};

const TestingDashboardLoginForm = forwardRef<ITestingDashboardLoginForm, ITestingDashboardLoginFormProps>((props, ref) => {
  const { onSubmit } = props;

  const theme = useTheme();
  const intl = useIntl();
  const formik = useFormik({
    initialValues: GetTestingDashboardLoginFormInitialValue(),
    validationSchema: getTestingDashBoardLoginFormSchema(intl),
    onSubmit,
    initialTouched: {
      userId: true,
      password: true,
    },
  });
  const enterToSubmit = useEnterToSubmit(formik);

  useImperativeHandle(
    ref,
    () => ({
      handleSubmit: formik.handleSubmit,
    }),
    [formik.handleSubmit],
  );

  return (
    <FormikProvider value={formik}>
      <Typography variant="h6" mt={1} mb={1}>
        <FormattedMessage id="testing-dashboard-login" />
      </Typography>
      <LoginForm data-testid="form" onKeyDown={enterToSubmit}>
        <LoginTextField
          formProps={formik}
          textFieldProps={{
            variant: 'filled',
            name: 'userId',
            inputProps: inputStyleUserName,
            InputLabelProps: {
              style: { color: theme.palette.primary.dark },
            },
          }}
        />
        <LoginTextField
          formProps={formik}
          textFieldProps={{
            variant: 'filled',
            name: 'password',
            type: 'password',
            inputProps: inputStylePassword,
            InputLabelProps: {
              style: { color: theme.palette.primary.dark },
            },
          }}
        />
      </LoginForm>
    </FormikProvider>
  );
});

interface ITestingDashboardLoginForm {
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
}

interface ITestingDashboardLoginFormProps {
  onSubmit: (
    values: ITestingDashboardLoginFormFields,
    formikHelpers: FormikHelpers<ITestingDashboardLoginFormFields>
  ) => void | Promise<any>;
}

export type { ITestingDashboardLoginForm, ITestingDashboardLoginFormProps };
export { TestingDashboardLoginForm };
